import { PortHoliday } from "modules/PortHolidays/usePortHolidayAPI"
import { useItineraryContext } from "modules/PortPredictor/hooks/itineraryContext"
import { PortType } from "modules/PortPredictor/portPredictorReducer"

import { useEffect, useState } from "react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import {
  highlightDays,
  PortPredictorPortTypeSelectOption,
} from "./PortPredictorArrivalInputs"

import { getFootnote } from "../PortPredictorDatePickerHelpers"

interface PortPredictorDatePickerProps {
  currMarkerLaycanStart: string | undefined
  currMarkerLaycanEnd: string | undefined
  portHolidays: PortHoliday[] | undefined
  chosenPortType: PortPredictorPortTypeSelectOption
  updateLaycan: ({ date, isStart }: { date: Date; isStart: boolean }) => void
  arrivalDate: string | undefined
  turnAroundTime: number | undefined
  errors: string[] | undefined
}

export function PortPredictorDatePicker({
  currMarkerLaycanStart,
  currMarkerLaycanEnd,
  portHolidays,
  updateLaycan,
  chosenPortType,
  arrivalDate,
  turnAroundTime,
}: PortPredictorDatePickerProps) {
  const { getTabStateKeyValue } = useItineraryContext()

  const [laycanStartDate, setLaycanStartDate] = useState<Date | null>(
    currMarkerLaycanStart ? new Date(currMarkerLaycanStart) : null
  )
  const [endDate, setEndDate] = useState<Date | null>(
    currMarkerLaycanEnd ? new Date(currMarkerLaycanEnd) : null
  )

  const [viewedStartDate, setViewedStartDate] = useState<Date>(new Date())
  const [viewedEndDate, setViewedEndDate] = useState<Date>(new Date())

  useEffect(() => {
    if (!arrivalDate) {
      return
    }

    setViewedStartDate(new Date(arrivalDate))
  }, [arrivalDate])

  const label =
    chosenPortType.value === PortType.Load ? "Laycan" : "Arrival Window"

  useEffect(() => {
    if (!laycanStartDate) {
      return
    }
    updateLaycan({ date: laycanStartDate, isStart: true })
  }, [laycanStartDate])

  useEffect(() => {
    if (!endDate) {
      return
    }
    updateLaycan({ date: endDate, isStart: false })
  }, [endDate])

  return (
    <div>
      <div className="d-flex align-items-center">
        <div className="PortPredictor_FormLabel fw-bold">{label}</div>
        <div className="PortPredictor_DatePicker_Wrapper">
          <DatePicker
            disabled={!getTabStateKeyValue("isEditMode")}
            dateFormat="dd/MM/yyyy"
            selectsStart
            placeholderText="Start"
            selected={laycanStartDate}
            onChange={(date) => setLaycanStartDate(date)}
            startDate={laycanStartDate}
            endDate={endDate}
            dayClassName={(date) =>
              highlightDays({
                date,
                portHolidaysData: portHolidays,
                turnAroundTime,
                arrivalDate,
              })
            }
            openToDate={
              laycanStartDate
                ? laycanStartDate
                : arrivalDate
                ? new Date(arrivalDate)
                : undefined
            }
            onMonthChange={(date: Date) => {
              setViewedStartDate(date)
            }}
            onCalendarOpen={() => {
              const initialMonth =
                laycanStartDate || viewedStartDate || new Date()
              setViewedStartDate(initialMonth)
            }}
          >
            <div className="PortPredictor_DatePicker_Footnote_Wrapper">
              <div>{getFootnote({ portHolidays, date: viewedStartDate })}</div>
            </div>
          </DatePicker>

          <DatePicker
            disabled={!getTabStateKeyValue("isEditMode")}
            className="PortPredictor_DatePicker_Input"
            dateFormat="dd/MM/yyyy"
            selectsEnd
            placeholderText="End"
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            startDate={laycanStartDate}
            endDate={endDate}
            minDate={laycanStartDate}
            dayClassName={(date) =>
              highlightDays({
                date,
                portHolidaysData: portHolidays,
                turnAroundTime,
                arrivalDate,
              })
            }
            // openToDate={endDate ? endDate : viewedStartDate}
            onMonthChange={(date: Date) => {
              setViewedEndDate(date)
            }}
            onCalendarOpen={() => {
              const initialMonth =
                laycanStartDate || viewedStartDate || new Date()

              setViewedEndDate(initialMonth)
            }}
          >
            <div>{getFootnote({ portHolidays, date: viewedEndDate })}</div>
          </DatePicker>
        </div>
      </div>
      <div className="d-flex justify-content-end">
        <div className="fs-sm fc-gray">Vessel events calendar</div>
      </div>
    </div>
  )
}
